<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    fields: Array,
    nbItems: {
      type: Number,
      default: 0,
    },
    defaultSort: {
      type: String,
    },
    defaultSortDesc: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    headVariant: {
      type: String,
      default: 'light',
    },
    border: {
      type: Boolean,
      default: false,
    },
    borderless: {
      type: Boolean,
      default: false,
    },
    noTopBorder: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: 'white',
    },
  },
  components: {
    Loading,
  },
  data() {
    return {
      currentContext: null,
    };
  },
  created() {
    this.currentContext = {
      sortBy: this.defaultSort,
      sortDesc: this.defaultSortDesc,
      currentPage: this.currentPage,
      perPage: this.itemsPerPage,
    };
  },
  methods: {
    sortChanged(ctx) {
      if (this.currentContext != null) {
        if (this.currentContext.sortBy == ctx.sortBy) {
          ctx.sortDesc = !this.currentContext.sortDesc;
        }
      }

      this.currentContext.sortBy = ctx.sortBy;
      this.currentContext.sortDesc = ctx.sortDesc;

      this.$emit('context-changed', this.currentContext);
    },
    itemsPerPageChanged(newValue) {
      this.currentContext.perPage = newValue;
      this.currentContext.currentPage = 1;
      this.$emit('context-changed', this.currentContext);
    },
    pageChanged(bvEvent, newValue) {
      this.currentContext.currentPage = newValue;
      this.$emit('context-changed', this.currentContext);
    },
    //This is needed so the grid don't handle the pages itself. It disconnects the current page property from the shown list.
    //It expect a page filtered list. So on page 2 it still show the full list which is calculated in the parent.
    async dataProvider() {
      return this.items;
    },
  },
  computed: {
    gridConfigText() {
      return 'gridConfig-' + this.id;
    },
    gridConfig: {
      get() {
        return this.$store.getters['localstorage/getDynamicConfigs'](
          this.gridConfigText
        );
      },
      set(value) {
        this.$store.dispatch('localstorage/setDynamicConfig', {
          code: this.gridConfigText,
          config: value,
        });
      },
    },
    itemsPerPage: {
      get() {
        return this.gridConfig.itemsPerPage;
      },
      set(value) {
        this.gridConfig = {
          ...this.gridConfig,
          itemsPerPage: value,
        };
      },
    },
    hiddenColumns() {
      return this.gridConfig.hiddenColumns;
    },
    fieldsFiltered() {
      return this.fields.filter((x) => !this.hiddenColumns.includes(x.key));
    },
  },
  watch: {
    items() {
      if (this.$refs[this.id] != undefined) {
        this.$refs[this.id].refresh();
      }
    },
  },
};
</script>

<template>
  <div style="position: relative">
    <loading
      :active="isBusy"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <div
      v-if="items != null"
      class="d-flex flex-column customScrollbar"
      style="overflow-x: scroll"
      :style="{ 'background-color': backgroundColor }"
      :class="{ 'border-around': border }"
    >
      <b-table
        :id="id"
        :items="dataProvider"
        :fields="fieldsFiltered"
        sort-icon-left
        thead-class="nowrap-header"
        :thead-tr-class="{ 'no-border-top': noTopBorder }"
        hover
        :borderless="borderless"
        no-local-sorting
        :head-variant="headVariant"
        class="mb-0"
        @sort-changed="sortChanged"
        :ref="id"
      >
        <template
          v-for="(x, slotName) in $scopedSlots"
          v-slot:[slotName]="context"
        >
          <slot :name="slotName" v-bind="context" />
        </template>
      </b-table>
    </div>
    <b-container v-show="nbItems > 0" fluid class="mt-2">
      <b-row>
        <b-col md="6" offset-md="3">
          <b-pagination
            class="mb-0"
            :value="currentPage"
            :total-rows="nbItems"
            :per-page="itemsPerPage"
            :aria-controls="id"
            align="center"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
            @page-click="pageChanged"
            first-number
            last-number
          >
          </b-pagination>
        </b-col>
        <b-col align-self="center">
          <b-form-group
            class="d-flex justify-content-end mb-0"
            :label="$t('general.itemPerPage')"
            label-for="pagesize-input"
          >
            <b-form-select
              class="ml-2"
              id="pagesize-input"
              label-field="Per page"
              v-model="itemsPerPage"
              :options="[10, 50, 100]"
              @change="itemsPerPageChanged"
              size="sm"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<style scoped>
.border-around {
  border: 1px solid #eff2f7;
}
</style>

<style>
.no-border-top > th {
  border-top: none !important;
}
.nowrap-header > tr > th {
  white-space: nowrap !important;
}
</style>